import ActionsPopOverItem from "@/components/ActionsPopOver/ActionsPopOverItem";
import ActionsPopover from "@/components/ActionsPopOver/ActionsPopOver";
import ImpactPill from "@/components/Impact/ImpactPill";
import MoreButton from "@/components/MoreButton/MoreButton";
import PageButtonGroup from "@/components/PageButtonGroup/PageButtonGroup";
import { PageHeader } from "@/components/PageHeader/PageHeader";
import StatusTag from "@/components/StatusTag/StatusTag";
import StatusTags from "@/components/StatusTags/StatusTags";
import Table, { Column, TableProps } from "@/components/Table/Table";
import useTableStateUrl from "@/components/Table/hooks/useTableStateUrl";
import TableHeader from "@/components/TableHeader/TableHeader";
import YearSelector from "@/components/YearSelector/YearSelector";
import CrossReference from "@/components/links/CrossReference/CrossReference";
import useTrackOnLoad from "@/hooks/useTrackPageview";
import useTranslation from "@/hooks/useTranslation";
import {
  Route,
  useGetOutboundTransportDetail,
  useGetOutboundYearsPerRoute,
} from "@/hooks/useTransport";
import { useDashboardYear } from "@/pages/dashboard/hooks/useDateRange";
import {
  BranchesOutlined,
  DeleteOutlined,
  ExportOutlined,
  EditOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { Alert, Button, Tooltip } from "antd";
import { useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import DeleteOutboundTransportAction from "../DeleteOutboundTransportAction";
import ExportOutboundTransportAction from "../ExportOutboundTransportAction";
import RouteRow from "../RouteRow/RouteRow";
import OutboundRouteWizard from "../RouteWizard/OutboundRouteWizard";
import OutboundOrderRouteWizard from "../RouteWizard/OutboundOrderRouteWizard";
import { transportUploadFilters } from "../constants";
import OutboundTransportButtonGroup from "./OutboundTransportButtonGroup";

export default function OutboundTransportDetail() {
  const translation = useTranslation();

  useTrackOnLoad("Outbound Transport Route Viewed");
  const { search: queryParams } = useLocation();

  const urlParams = useParams();
  const departureFacilityId = Number(urlParams.departureId);

  const arrivalFacility = new URLSearchParams(queryParams).get("arrival_facility") === "true";
  const arrivalName = !arrivalFacility ? urlParams.arrivalName || "" : "";
  const arrivalFacilityId = arrivalFacility ? Number(urlParams.arrivalName) : undefined;
  const { year } = useDashboardYear();

  const byCategory = new URLSearchParams(queryParams).get("by_category") === "true";
  const [isMoreOpen, setIsMoreOpen] = useState(false);

  const transportDetailColumns: Column[] = [
    {
      key: "order_id",
      label: translation.transport.orderID,
      searchable: true,
      maxWidth: "200px",
      renderer: (record) => (
        <CrossReference to={`/orders/sales_orders?search_order_id=${record.order_id}`}>
          {record.order_id}
        </CrossReference>
      ),
    },
    {
      key: "departure_address",
      label: translation.transport.departureAddress,
      maxWidth: "2fr",
    },
    {
      key: "arrival_address",
      label: translation.transport.arrivalAddress,
      maxWidth: "2fr",
    },
    {
      key: "transport_mode",
      label: translation.transport.modeOfTransport,
    },
    {
      key: "owned",
      label: "Owned or leased",
      maxWidth: "150px",
      renderer: (record) => (record.owned ? "Yes" : "No"),
    },
    {
      key: "impact",
      label: "Impact",
      minWidth: "230px",
      maxWidth: "230px",
      renderer: (route) => {
        return (
          <ImpactPill
            impact={!route.route_error ? route.impact : undefined}
            type="borderless"
          />
        );
      },
    },
    {
      label: translation.transport.status,
      key: "status",
      maxWidth: "130px",
      minWidth: "130px",
      filterable: true,
      renderer: (route) => {
        return (
          <Tooltip
            title={
              route.status === "error"
                ? `${route.route_error} Try editing or uploading new data.`
                : route.status === "calculating"
                  ? "Root is calculating the impact of this leg in the background. All you have to do is wait. ⏳"
                  : undefined
            }
          >
            <>
              <StatusTag status={route.status} />
            </>
          </Tooltip>
        );
      },
    },
    {
      key: "actions",
      label: "Actions",
      maxWidth: "80px",
      minWidth: "80px",
      noLink: true,
      renderer: (route) => {
        return (
          <ActionsPopover
            actions={
              <OutboundOrderRouteWizard
                orderId={route.order_id}
                departureId={departureFacilityId}
                arrivalId={arrivalFacilityId}
                arrivalName={arrivalName}
                byTransportCategory={byCategory}
                year={year}
              >
                <ActionsPopOverItem icon={<EditOutlined />}>Edit</ActionsPopOverItem>
              </OutboundOrderRouteWizard>
            }
          >
            <MoreOutlined />
          </ActionsPopover>
        );
      },
      align: "end",
    },
  ];

  const { pagination, filter, search, sort, onSearchFilterSortPaginate, setPage } =
    useTableStateUrl({
      columns: transportDetailColumns,
    });

  const { data, isLoading, isFetching, error } = useGetOutboundTransportDetail({
    year,
    departureFacilityId,
    arrivalFacilityId,
    arrivalName,
    byTransportCategory: byCategory,
    pagination,
    filter,
    search,
    sort,
  });

  const { data: years, isLoading: yearsLoading } = useGetOutboundYearsPerRoute({
    departureId: departureFacilityId,
    arrivalId: arrivalFacilityId,
    arrivalName: arrivalName,
    byTransportCategory: byCategory,
  });

  if (error) {
    return (
      <Alert
        message="Error"
        description="An error occurred while loading the outbound transport details. Please try again later."
        type="error"
        showIcon
      />
    );
  }

  const connectionWord = byCategory ? "in" : "to";
  const title = data
    ? data.arrival_name
      ? `Outbound transport from ${data.departure_name} ${connectionWord} ${data.arrival_name}`
      : `Outbound transport from ${data.departure_name} ${translation.transport.withoutCategory}`
    : "Loading...";

  const tableProps: TableProps<Route> = {
    columns: transportDetailColumns,
    availableFilters: transportUploadFilters,
    rowKey: "order_id",
    onSearchFilterSortPaginate,
    loading: isLoading || isFetching,
    data: data?.routes,
    totalRows: data?.total_count,
    pagination,
    sort,
    filter,
    search,
    rowRenderer: RouteRow,
    hideRaster: true,
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <PageHeader
        loading={isLoading}
        title={title}
        right={<OutboundTransportButtonGroup />}
      />
      <div>
        <TableHeader
          left={
            <>
              <YearSelector
                optionData={years}
                isLoading={yearsLoading}
                onChange={() => setPage(1)}
              />
              <PageButtonGroup>
                <OutboundRouteWizard
                  arrivalName={arrivalName}
                  departureId={departureFacilityId}
                  arrivalId={arrivalFacilityId}
                  byTransportCategory={byCategory}
                  year={year}
                >
                  <Button icon={<BranchesOutlined />}>Edit</Button>
                </OutboundRouteWizard>
                <ExportOutboundTransportAction
                  route={{
                    by_transport_category: byCategory,
                    departure_facility_id: departureFacilityId,
                    arrival_facility_id: arrivalFacilityId,
                    arrival_name: arrivalName,
                  }}
                  year={year}
                >
                  <Button icon={<ExportOutlined />}>Export</Button>
                </ExportOutboundTransportAction>
                <MoreButton
                  onOpenChange={setIsMoreOpen}
                  open={isMoreOpen}
                  zIndex={3}
                  actions={
                    <>
                      <DeleteOutboundTransportAction
                        route={{
                          by_transport_category: byCategory,
                          departure_facility_id: departureFacilityId,
                          arrival_facility_id: arrivalFacilityId,
                          arrival_name: arrivalName,
                        }}
                        year={year}
                        onSuccess={() => setIsMoreOpen(false)}
                      >
                        <ActionsPopOverItem
                          icon={<DeleteOutlined />}
                          danger
                        >
                          Delete
                        </ActionsPopOverItem>
                      </DeleteOutboundTransportAction>
                    </>
                  }
                />
              </PageButtonGroup>
            </>
          }
          right={
            <StatusTags
              data={data}
              loading={isLoading}
              calculatingTooltip="Root is calculating the impact of some transport legs in this route in the background. All you have to do is wait. ⏳"
              errorTooltip="Root has not been able to calculate the impact of some legs in this route. Hover over the error in a leg to see what's going wrong. 🧐"
            />
          }
        />
        <Table {...tableProps} />
      </div>
    </div>
  );
}
